import { api } from '../_client';

/**
 * The one Error shape every upload route throws.
 *
 * Carries the RFC 7807 `error_code` onto the thrown Error: the batch uploader
 * tells a skipped duplicate from a real failure by code, not by message text
 * (#1172) — dropping it here is what forced the prose match upstream. Shared by
 * the path-addressed `uploadFile` and the id-addressed `uploadIntoObject`
 * (#1351) so the two routes are classified by one rule.
 */
export function toUploadError(err: unknown): Error & { errorCode?: string } {
  let errorMessage = 'Upload failed';
  let errorCode: string | undefined;
  if (err && typeof err === 'object') {
    if (
      'response' in err &&
      err.response &&
      typeof err.response === 'object' &&
      'data' in err.response
    ) {
      const data = err.response.data as { detail?: string; error_code?: string };
      errorMessage = data.detail || errorMessage;
      errorCode = data.error_code;
    } else if ('message' in err && typeof err.message === 'string') {
      errorMessage = err.message;
    }
  }
  const uploadError: Error & { errorCode?: string } = new Error(errorMessage);
  uploadError.errorCode = errorCode;
  return uploadError;
}

export const filesUploadApi = {
  // Upload file to S3
  async uploadFile(
    file: File,
    metadata: {
      destinationPath: string;
      flightType: string;
      payloadId: number;
      pilotId: string;
      customerId?: string;
      projectId?: string;
      airspaceObserverId?: string;
      payloadOperatorId?: string;
      bypassValidation?: boolean;
    }
  ): Promise<{
    success: boolean;
    file_url: string;
    flight_id?: string;
    metadata: Record<string, unknown>;
    error?: string;
    /**
     * Non-fatal notices about an upload that SUCCEEDED — hours that could not be
     * extracted, or a file stored but not filed into Files (#1239). The backend
     * has always sent these and nothing read them until #1253, so a qualified
     * 200 looked identical to a clean one. Declared here because a field this
     * type omits is a field the uploader cannot see.
     */
    warnings?: { code: string; message: string }[];
    /**
     * The `fleet.file_objects` row this upload became (STD-029 D-9b).
     *
     * Optional forever, and not defensively: an object whose registration
     * soft-failed has no row and therefore no id (R-9). Declared here because a
     * field this type omits is a field the uploader cannot see — the mistake
     * that made `object_id` invisible to three client declarations at once
     * (#1324 root-cause chain, R-3).
     */
    object_id?: string | null;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('destination_path', metadata.destinationPath);
    if (metadata.flightType) {
      formData.append('flight_type', metadata.flightType);
    }
    if (metadata.payloadId) {
      formData.append('payload_id', metadata.payloadId.toString());
    }
    if (metadata.pilotId) {
      formData.append('pilot_id', metadata.pilotId);
    }
    if (metadata.customerId) {
      formData.append('customer_id', metadata.customerId);
    }
    if (metadata.projectId) {
      formData.append('project_id', metadata.projectId);
    }
    if (metadata.airspaceObserverId) {
      formData.append('airspace_observer_id', metadata.airspaceObserverId);
    }
    if (metadata.payloadOperatorId) {
      formData.append('payload_operator_id', metadata.payloadOperatorId);
    }
    if (metadata.bypassValidation !== undefined) {
      formData.append('bypass_validation', metadata.bypassValidation.toString());
    }

    try {
      const response = await api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for large file uploads
      });

      return response.data;
    } catch (err: unknown) {
      throw toUploadError(err);
    }
  },

  // Analyze flight file
  async analyzeFile(
    file: File,
    flightType?: string
  ): Promise<{
    success: boolean;
    metadata: Record<string, unknown>;
    rows_processed: number;
    columns_processed: number;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    if (flightType) {
      formData.append('flight_type', flightType);
    }

    const response = await api.post('/api/files/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for analysis
    });

    return response.data;
  },

  // Get download URL
  async getDownloadUrl(filePath: string): Promise<{
    download_url: string;
    expires_in: number;
  }> {
    const response = await api.get(`/api/files/${encodeURIComponent(filePath)}/download`);
    return response.data;
  },

  // --- RETIRED: the path-addressed write calls (STD-029 R-11, arc B-3) ------
  //
  // `createFolder`, `moveFile`, `moveFolder`, `deleteFile`, `deleteFolder` and
  // `renameFolder` lived here and are gone. Their id-addressed replacements are
  // on `fileObjectsApi` (`services/api/fileObjects.ts`):
  //
  //     createFolder(path)             -> createFolderObject({parentId, name})
  //     moveFile / moveFolder(k, dest) -> updateObject(id, {parentId})
  //     renameFolder(key, name)        -> updateObject(id, {name})
  //     deleteFile(path)               -> deleteObject(id)
  //     deleteFolder(path)             -> deleteObject(id, {recursive: true})
  //
  // The five backend handlers behind them were deleted in the same PR, so these
  // are not deprecated wrappers — there is nothing left to call. What made the
  // conversion necessary rather than tidy: a path is a coordinate in the
  // caller's own tree, so none of them could ever reach a folder another
  // organization shared, which is why a `write` share was inert on this surface
  // (D-4).
  //
  // `getDownloadUrl` above is deliberately NOT in that list. Downloads are the
  // READ surface and retire with the browse projection in B-4; the shared-object
  // case already has its id-addressed twin in `getObjectDownloadUrl`.
};
